import { Filter } from 'types';

import {
  NewCentralFormType,
  SearchSelectedItems,
  SpecificFilterRow,
} from '../types';
import _ from 'lodash';
import {
  InitialBody,
  ParamsMap,
  SearchEntityEnum,
  searchEntityTypeList,
} from './search-v3-types';
import {
  createParamsMap,
  getAllValidSearchSpaces,
  getEmptyParamsMap,
  getNegationFilter,
  getTextFilter,
  getWordsAndOperation,
  keepValidSpacesOnly,
  mergeParamsMap,
} from './search-v3-utils';
import { formatComplexDateFilter } from './filter-formatters/search-v3-format-complex-date';

export const formatSearchFiltersv3 = (
  values: NewCentralFormType
): InitialBody => {
  const customRows = values.specificFilterRows.filter(
    (f) => f.filterOption.filterType === 'CUSTOM'
  );
  const ftxRows = values.specificFilterRows.filter(
    (f) => f.filterOption.filterType === 'FTX'
  );
  const textRows = values.specificFilterRows.filter(
    (f) => f.filterOption.filterType === 'TEXT'
  );
  const relationRows = values.specificFilterRows.filter(
    (f) => f.filterOption.filterType === 'RELATION'
  );

  const relationV2Rows = values.specificFilterRows.filter(
    (f) => f.filterOption.filterType === 'RELATION_V2'
  );
  const timeRangeRows = values.specificFilterRows.filter(
    (f) => f.filterOption.filterType === 'TIME_RANGE'
  );

  const complexTextRows = values.specificFilterRows.filter(
    (f) => f.filterOption.filterType === 'COMPLEX_TEXT'
  );

  const complexDateRows = values.specificFilterRows.filter(
    (f) => f.filterOption.filterType === 'COMPLEX_DATE'
  );

  const registerClasses = values.specificFilterRows.filter(
    (f) => f.filterOption.filterType === 'REGISTER_CLASSES'
  );

  /*******DONE ********/
  const formattedCustomRows: ParamsMap[] = (customRows
    .map((row) => row?.filterOption?.filterFormatter?.(row))
    .filter((f) => !!f) ?? []) as ParamsMap[];

  // collect all cascading filters on filter type CUSTOM as it is not done by formatter
  const formattedCustomRowsCascading: ParamsMap[] = (customRows
    .filter((r) => r.filterOption.cascading)
    .map((row) => row?.filterOption?.filterFormatter?.(row))
    .filter((f) => !!f) ?? []) as ParamsMap[];

  const formattedFTXFilters = formatFTXFilters(
    ftxRows,
    values.archivalUnitsFilters.digital
  );
  const formattedTextFilters = formatTextFilters(textRows);
  const formattedTimeRangeFilters = formatTimeRangeFilters(timeRangeRows);
  const formattedComplexTextFilters = formatComplexTextFilter(complexTextRows);
  const formattedRegisterClassesFilters = formatRegisterClassesFilters(
    registerClasses
  );

  const formattedRelationsFilters = formatRelationsFilters(relationRows);
  const formattedRelationsV2Filters = formatRelationsFiltersV2(relationV2Rows);
  const formattedComplexDateFilters = formatComplexDateFilter(complexDateRows);

  const selectedCollections = formatSelectedCollectionsFilter(values);
  const selectedAids = formatSelectedAidsFilter(values);
  const archivalKinds = formatEvidenceUnitKindFilter(values);

  /*********** ********/
  const sp = getAllValidSearchSpaces(values);

  const paramsMap = mergeParamsMap([
    formattedTextFilters?.paramsMap,
    formattedTimeRangeFilters?.paramsMap,
    formattedComplexTextFilters,
    formattedFTXFilters,
    formattedRegisterClassesFilters,
    formattedRelationsFilters,
    formattedRelationsV2Filters,
    formattedComplexDateFilters,
    ...formattedCustomRows,
    sp.includes(SearchEntityEnum.ARCHIVAL_COLLECTION)
      ? selectedCollections
      : {},
    sp.includes(SearchEntityEnum.ARCHIVAL_AID) ? selectedAids : {},
    sp.includes(SearchEntityEnum.EAD_NODE_UNIT) ? archivalKinds : {},
  ]);

  const cascadingParamsMap = mergeParamsMap([
    formattedTextFilters?.cascadingParamsMap,
    formattedTimeRangeFilters?.cascadingParamsMap,
    ...formattedCustomRowsCascading,
  ]);

  const validParamsMap = keepValidSpacesOnly(values, paramsMap);

  searchEntityTypeList.forEach((entityType) => {
    if (!values.includeSearchEntityTypes_V3[entityType]) {
      validParamsMap[entityType] = undefined;
      cascadingParamsMap[entityType] = undefined;
    }
  });

  return { paramsMap: validParamsMap, cascadingParamsMap };
};

// CUSTOM
export const formatReferenceNameAndValueRow = (
  row: SpecificFilterRow
): ParamsMap | undefined => {
  if (!!!row?.customFilterValues?.[1]?.value) {
    return undefined;
  }
  const paramsMap = getEmptyParamsMap();

  const referenceType = row?.customFilterValues?.[0]?.id ?? undefined;

  if (referenceType) {
    const filter = {
      operation: 'OR',
      filters: [
        {
          operation: 'NESTED',
          path: 'eadNode.referenceLabels',
          filter: {
            operation: 'AND',
            filters: [
              {
                operation: 'EQ',
                field: 'referenceLabels.type',
                value: referenceType,
                nestedQueryEnabled: false,
              },
              {
                operation: 'CONTAINS',
                field: 'referenceLabels.value',
                value: row?.customFilterValues?.[1]?.value ?? '',
                nestedQueryEnabled: false,
              },
            ],
          },
        },
        {
          operation: 'NESTED',
          path: 'eadNode.referenceLabels',
          filter: {
            operation: 'AND',
            filters: [
              {
                operation: 'EQ',
                field: 'eadNode.referenceLabels.type',
                value: referenceType,
                nestedQueryEnabled: false,
              },
              {
                operation: 'CONTAINS',
                field: 'eadNode.referenceLabels.value',
                value: row?.customFilterValues?.[1]?.value ?? '',
                nestedQueryEnabled: false,
              },
            ],
          },
        },
      ],
    };
    paramsMap.EAD_NODE_UNIT?.push(getNegationFilter(filter, !!row.negation));
  } else {
    const filter = {
      operation: 'OR',
      filters: [
        {
          operation: 'CONTAINS',
          field: 'referenceLabels.value',
          value: row?.customFilterValues?.[1]?.value ?? '',
          nestedQueryEnabled: false,
        },
        {
          operation: 'CONTAINS',
          field: 'eadNode.referenceLabels.value',
          value: row?.customFilterValues?.[1]?.value ?? '',
          nestedQueryEnabled: false,
        },
      ],
    };

    paramsMap.EAD_NODE_UNIT?.push(getNegationFilter(filter, !!row.negation));
  }

  return paramsMap;
};

export const formatOriginatorIdentifiers = (
  row: SpecificFilterRow
): ParamsMap | undefined => {
  if (!!!row?.customFilterValues?.[1]?.value) {
    return undefined;
  }
  const paramsMap = getEmptyParamsMap();

  const identifierType = row?.customFilterValues?.[0]?.id ?? undefined;
  const identifierValue = row?.customFilterValues?.[1]?.value ?? undefined;

  if (identifierType) {
    const filter = {
      operation: 'AND',
      filters: [
        {
          operation: 'EQ',
          field: 'identifiers.type',
          value: identifierType,
        },
        {
          operation: 'CONTAINS',
          field: 'identifiers.value',
          value: identifierValue,
        },
      ],
    };
    paramsMap.ORIGINATOR?.push(getNegationFilter(filter, !!row.negation));
  } else {
    const filter = {
      operation: 'CONTAINS',
      field: 'identifiers.value',
      value: identifierValue,
    };

    paramsMap.ORIGINATOR?.push(getNegationFilter(filter, !!row.negation));
  }

  return paramsMap;
};

const formatFTXFilters = (
  rows: SpecificFilterRow[],
  includeTextLayer?: boolean
): ParamsMap | undefined => {
  if (!rows || rows.length < 1) {
    return undefined;
  }
  const paramsMap = getEmptyParamsMap();

  Object.keys(paramsMap).forEach((key) => {
    const pkey = key as SearchEntityEnum;

    paramsMap[pkey] = rows
      .filter((row) => !!row.ftxFilterValue?.value)
      .map((row) => {
        const ftxType = row.ftxFilterValue?.ftxType ?? 'FTX';
        const body = includeTextLayer
          ? {
              operation: 'OR',
              filters: [
                {
                  field: 'textLayer',
                  operation: 'FTXF',
                  value: row.ftxFilterValue?.value,
                },
                {
                  operation: ftxType,
                  value: row.ftxFilterValue?.value,
                },
              ],
            }
          : {
              operation: ftxType,
              value: row.ftxFilterValue?.value,
            };

        return getNegationFilter(body, !!row.negation);
      });
  });

  return paramsMap;
};

const formatComplexTextFilter = (
  rows: SpecificFilterRow[]
): ParamsMap | undefined => {
  if (!rows || rows.length < 1) {
    return undefined;
  }

  const formatted: ParamsMap[] = rows.map((row) => {
    const { textOperation, words } = getWordsAndOperation(
      row.complexTextFilterValue?.textOperation,
      row.complexTextFilterValue?.value
    );
    const paramsMap = getEmptyParamsMap();
    Object.keys(row?.filterOption?.targetFieldsComplex_v3 ?? {}).forEach(
      (key) => {
        const pkey = key as SearchEntityEnum;
        const targetFields = row.filterOption.targetFieldsComplex_v3?.[
          pkey
        ] as string[];

        const filters: Filter[] = targetFields.map((field) => {
          return getTextFilter(field, words, textOperation);
        });
        paramsMap[pkey] = [
          getNegationFilter({ operation: 'OR', filters }, !!row.negation),
        ];
      }
    );

    return paramsMap;
  });

  const result = mergeParamsMap(formatted);

  return result;
};

///////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////TEXT FILTERS//////////////////////////////////////

const getWordSegmenter = () => {
  return new Intl.Segmenter(undefined, { granularity: 'word' });
};
const getWords = (text: string) => {
  const segmenter = getWordSegmenter();
  const segments = segmenter.segment(text);
  return Array.from(segments)
    .filter((segment) => segment.isWordLike)
    .map((segment) => segment.segment);
};

const formatTextFilters = (
  rows: SpecificFilterRow[]
): InitialBody | undefined => {
  if (!rows || rows.length < 1) {
    return undefined;
  }
  const formatted: Filter[] = rows.map((row) => {
    if (row.textFilterValue?.textOperation === 'CONTAINS_AND_CONTAINS') {
      const words = getWords(row.textFilterValue.value);

      const result = words
        .filter((w) => w)
        .map((w) => ({
          operation: 'OR',
          filters: row.textFilterValue?.fields.map((field) => ({
            field,
            operation: 'CONTAINS',
            value: w,
            ...(row.filterOption.nestedQueryEnabled === false
              ? { nestedQueryEnabled: false }
              : {}),
          })),
        }));
      return { operation: 'AND', filters: result };
    }

    if (row.textFilterValue?.textOperation === 'CONTAINS') {
      const words = getWords(row.textFilterValue.value).filter((w) => w) ?? [];
      const result = words.map((w) => ({
        operation: 'OR',
        filters: row.textFilterValue?.fields.map((field) => ({
          field,
          operation: 'CONTAINS',
          value: w,
          ...(row.filterOption.nestedQueryEnabled === false
            ? { nestedQueryEnabled: false }
            : {}),
        })),
      }));
      return { operation: 'OR', filters: result };
    }
    if (row.textFilterValue?.textOperation === 'MATCH_PHRASE') {
      return {
        operation: 'OR',
        filters: row.textFilterValue?.fields.map((field) => ({
          field,
          operation: 'MATCH_PHRASE',
          value: row.textFilterValue?.value,
          ...(row.filterOption.nestedQueryEnabled === false
            ? { nestedQueryEnabled: false }
            : {}),
        })),
      };
    }

    return {
      operation: 'OR',
      filters: row.textFilterValue?.fields.map((field) => ({
        field,
        operation: 'EQ',
        value: row.textFilterValue?.value,
        ...(row.filterOption.nestedQueryEnabled === false
          ? { nestedQueryEnabled: false }
          : {}),
      })),
    };
  });

  return createParamsMap(formatted, rows);

  // return formatted.map((f, index) => {
  //   const filter = getNegationFilter(f, !!rows[index].negation);
  //   return (
  //     rows[index].filterOption?.category?.filterWrapper?.(filter) ?? filter
  //   );
  // });
};
///////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////
const formatRegisterClassesFilters = (
  classesRows: SpecificFilterRow[]
): ParamsMap | undefined => {
  if (classesRows && classesRows.length <= 0) {
    return undefined;
  }

  const paramsMap = getEmptyParamsMap();

  paramsMap.ORIGINATOR = classesRows.map((row) => {
    const filter: Filter = {
      operation: 'CONTAINS',
      field: 'subClass',
      value: row.registerClassesFilterValue?.subClass.id,
    };

    return getNegationFilter(filter, !!row.negation);
  });
  return paramsMap;
  // return {
  //   operation: 'AND',
  //   filters: classesRows.map((row) => {
  //     const filter: Filter = {
  //       operation: 'AND',
  //       filters: [
  //         {
  //           field: 'archivalEntityType',
  //           operation: 'EQ',
  //           value: 'ORIGINATOR',
  //         },
  //         {
  //           operation: 'CONTAINS',
  //           field: 'subClass',
  //           value: row.registerClassesFilterValue?.subClass.id,
  //         },
  //       ],
  //     };
  //     return getNegationFilter(filter, !!row.negation);
  //   }),
  // };
};

// pre archivalie - rej. heslo v roli
const formatRelationsFilters = (
  relationRows: SpecificFilterRow[]
): ParamsMap | undefined => {
  if (relationRows && relationRows.length <= 0) {
    return undefined;
  }
  const valuePath = 'relationFilterValue.originator.label';
  const paramsMap = getEmptyParamsMap();

  relationRows.forEach((row) => {
    if (row.relationFilterValue?.relationRole?.id === '_ALL_ROLES') {
      const filter: Filter = {
        operation: 'OR',
        filters: [
          {
            operation: 'CONTAINS',
            field: 'relations.originator.label',
            value: _.get(row, valuePath),
            nestedQueryEnabled: false,
          },
          {
            operation: 'CONTAINS',
            field: 'eadNode.relations.originator.label',
            value: _.get(row, valuePath),
            nestedQueryEnabled: false,
          },
        ],
      };
      paramsMap.EAD_NODE_UNIT?.push(getNegationFilter(filter, !!row.negation));
    } else {
      const filter: Filter = {
        operation: 'OR',
        filters: [
          {
            operation: 'NESTED',
            path: 'relations',
            filter: {
              operation: 'AND',
              filters: [
                {
                  operation: 'CONTAINS',
                  field: 'relations.originator.label',
                  value: _.get(row, valuePath),
                  nestedQueryEnabled: false,
                },
                {
                  operation: 'EQ',
                  field: 'relations.label',
                  value: row.relationFilterValue?.relationRole?.label,
                  nestedQueryEnabled: false,
                },
              ],
            },
          },
          {
            operation: 'NESTED',
            path: 'eadNode.relations',
            filter: {
              operation: 'AND',
              filters: [
                {
                  operation: 'CONTAINS',
                  field: 'eadNode.relations.originator.label',
                  value: _.get(row, valuePath),
                  nestedQueryEnabled: false,
                },
                {
                  operation: 'EQ',
                  field: 'eadNode.relations.label',
                  value: row.relationFilterValue?.relationRole?.label,
                  nestedQueryEnabled: false,
                },
              ],
            },
          },
        ],
      };
      paramsMap.EAD_NODE_UNIT?.push(getNegationFilter(filter, !!row.negation));
    }
  });

  return paramsMap;
};

const formatRelationsFiltersV2 = (
  relationRows: SpecificFilterRow[]
): ParamsMap | undefined => {
  if (relationRows && relationRows.length <= 0) {
    return undefined;
  }
  const paramsMap = getEmptyParamsMap();

  relationRows.forEach((row) => {
    if (row.relationFilterValue?.relationRole?.id === '_ALL_ROLES') {
      const filter: Filter = {
        operation: 'OR',
        filters: [
          {
            operation: 'CONTAINS',
            field: 'relations.relatedEntity.label',
            value: row.relationFilterValue?.originator?.label,
            nestedQueryEnabled: false,
          },
        ],
      };
      paramsMap.ORIGINATOR?.push(getNegationFilter(filter, !!row.negation));
    } else {
      const filter: Filter = {
        operation: 'OR',
        filters: [
          {
            operation: 'NESTED',
            path: 'relations',
            filter: {
              operation: 'AND',
              filters: [
                {
                  operation: 'CONTAINS',
                  field: 'relations.relatedEntity.label',
                  value: row.relationFilterValue?.originator?.label,
                  nestedQueryEnabled: false,
                },
                {
                  operation: 'EQ',
                  field: 'relations.type',
                  value: row.relationFilterValue?.relationRole?.id,
                  nestedQueryEnabled: false,
                },
              ],
            },
          },
        ],
      };
      paramsMap.ORIGINATOR?.push(getNegationFilter(filter, !!row.negation));
    }
  });

  return paramsMap;
};

const formatTimeRangeFilters = (
  trf: SpecificFilterRow[]
): InitialBody | undefined => {
  if (!trf || trf.length <= 0) {
    return undefined;
  }
  const formatted = trf.map((row) => {
    const filters = [];

    if (row.timeRangeFilterValue?.from) {
      filters.push({
        operation: 'GTE',
        field: 'timeRangeTo',
        value: `${row.timeRangeFilterValue?.from}T00:00:00`,
      });
    }

    if (row.timeRangeFilterValue?.to) {
      filters.push({
        operation: 'LTE',
        field: 'timeRangeFrom',
        value: `${row.timeRangeFilterValue?.to}T23:59:59`,
      });
    }

    const filter = getNegationFilter(
      { operation: 'AND', filters },
      !!row.negation
    );

    return filter;
  });

  return createParamsMap(formatted, trf);
};

/*

filters.push({
      field: 'department.id',
      operation: ApiFilterOperation.IN,
      values: departmentIds,
    });*/

const formatSelectedEntitiesFilter = ({
  fieldsMap,
  selectedItems,
  nestedQueryEnabledFalse,
}: {
  fieldsMap: Record<string, string[]>;
  selectedItems: SearchSelectedItems;
  nestedQueryEnabledFalse?: boolean;
}): ParamsMap | undefined => {
  const operation = selectedItems.operation;
  const paramsMap = getEmptyParamsMap();

  Object.keys(fieldsMap).forEach((key) => {
    const filters: Filter[] = [];

    selectedItems.items.forEach((item) => {
      const fields = fieldsMap[key as SearchEntityEnum];
      fields.forEach((field) => {
        filters.push({
          operation: 'EQ',
          field,
          value: item.id,
          ...(nestedQueryEnabledFalse ? { nestedQueryEnabled: false } : {}),
        });
      });
    });

    if (filters.length <= 0) {
      return;
    }
    if (filters.length === 1) {
      const filter = getNegationFilter(filters[0], operation === 'exclude');
      paramsMap[key as SearchEntityEnum]?.push(filter);
    } else {
      const filter = getNegationFilter(
        { operation: 'OR', filters },
        operation === 'exclude'
      );
      paramsMap[key as SearchEntityEnum]?.push(filter);
    }
  });

  return paramsMap;
};

export const formatSelectedCollectionsFilter = (
  values: NewCentralFormType
): ParamsMap | undefined =>
  formatSelectedEntitiesFilter({
    fieldsMap: {
      [SearchEntityEnum.ARCHIVAL_AID]: ['archivalCollection.id'],
      [SearchEntityEnum.ARCHIVAL_COLLECTION]: ['id'],
      [SearchEntityEnum.EAD_NODE_UNIT]: ['archivalCollection.id'],
    },
    selectedItems: values.selectedCollections,
  });
export const formatSelectedAidsFilter = (
  values: NewCentralFormType
): ParamsMap | undefined =>
  formatSelectedEntitiesFilter({
    fieldsMap: {
      [SearchEntityEnum.ARCHIVAL_AID]: ['id'],
      [SearchEntityEnum.ARCHIVAL_COLLECTION]: ['describingArchivalAids.id'],
      [SearchEntityEnum.EAD_NODE_UNIT]: [
        'archivalAidMetadata.id',
        'describingArchivalAids.id',
      ],
    },
    selectedItems: values.selectedAids,
  });
export const formatEvidenceUnitKindFilter = (
  values: NewCentralFormType
): ParamsMap | undefined =>
  formatSelectedEntitiesFilter({
    fieldsMap: {
      [SearchEntityEnum.EAD_NODE_UNIT]: [
        'eadNode.evidenceUnits.unitKind',
        'evidenceUnits.unitKind',
      ],
    },
    selectedItems: values.selectedEvidenceUnitTypes,
    nestedQueryEnabledFalse: true,
  });
